import { DEFAULT_LINK_RULES, withLinkRules, type LinkRules } from '../ludus-lead/types.ts'

/**
 * `audit.toml` — the source of truth for an audit's parameters (architecture
 * §4.4, A-43). Seven sections, written only by the engine, denormalised into
 * the `audits` row for the catalogue and never the other way round.
 *
 * This module is the file's grammar: the typed document, the §6.6 defaults,
 * a parser for the canonical form the engine writes, and the writer that
 * produces it. **The writer is canonical**: fixed section order, fixed key
 * order, one value per line, sub-tables for the nested rules — so a document
 * round-trips byte-identically and two engines writing the same parameters
 * write the same bytes, which is what makes `[versions].rules` a hash worth
 * keeping.
 *
 * `[rules]` deserialises into `LinkRules` through `withLinkRules` — the file
 * carries the tenant's overrides in the file's own snake_case, and the layer
 * keeps its one type. Three keys have no `LinkRules` counterpart today
 * (`product_key_source`, `review_budget_hours`, `replay_from`) and stay on the
 * document alone; `multi_customer_tiebreak` is carried but its only value is
 * the layer's single-member union (E1.3).
 */

export type AuditStage =
  | 'snapshot'
  | 'transcribe'
  | 'pseudonymize'
  | 'classify_call'
  | 'extract_facts'
  | 'link'
  | 'qa_checklist'
  | 'segment'
  | 'marts'
  | 'export'

export const AUDIT_STAGES: readonly AuditStage[] = [
  'snapshot',
  'transcribe',
  'pseudonymize',
  'classify_call',
  'extract_facts',
  'link',
  'qa_checklist',
  'segment',
  'marts',
  'export',
] as const

export interface AuditRules {
  link_window_days: number
  open_cycle_reach_days: number
  call_only_lead: 'allow' | 'anomaly'
  product_key_source: string
  merge_overlapping_deals: boolean
  manager_extensions: string[]
  identifier_confidence: Record<string, number>
  multi_customer_tiebreak: 'first_seen'
  review_budget_hours: number
  /** An audit id of the same tenant whose decisions seed `link`; empty = off. */
  replay_from: string
  call_note_window_sec: number
  phone: { min_digits: number; default_calling_code: string; strip_trunk_prefix: boolean }
}

export interface AuditVersions {
  schema: number
  adapters: Record<string, { commit: string; slot_mapping: string }>
  rules: string
  materials: string
  prompts: Record<string, string>
}

export interface AuditToml {
  audit: { id: string; title: string; created_at: string }
  slice: {
    pipelines: string[]
    period_from: string
    period_to: string
    cutoff_at: string
    snapshot_at: string | null
    /** The tenant's fallback for the time norm (§8.9), `Mon-Fri 09:00-18:00 Europe/Rome`; `null` = none. */
    business_hours: string | null
  }
  stages: { enabled: AuditStage[]; test_run_n: number }
  providers: { asr: string | null; llm: string | null; llm_model: string | null }
  quota: { asr_minutes: number; llm_tokens: number; amount: number; currency: string }
  rules: AuditRules
  versions: AuditVersions
}

/** §6.6's defaults, with the two additions E1.3 recorded and the call-note window. */
export const DEFAULT_AUDIT_RULES: AuditRules = {
  link_window_days: DEFAULT_LINK_RULES.linkWindowDays,
  open_cycle_reach_days: DEFAULT_LINK_RULES.openCycleReachDays,
  // Ruling 2026-08-22: on a historical slice every call-only cycle goes to the queue.
  call_only_lead: 'anomaly',
  product_key_source: 'pipeline',
  merge_overlapping_deals: DEFAULT_LINK_RULES.mergeOverlappingDeals,
  manager_extensions: [],
  identifier_confidence: { ...DEFAULT_LINK_RULES.identifierConfidence },
  multi_customer_tiebreak: 'first_seen',
  review_budget_hours: 12,
  replay_from: '',
  call_note_window_sec: DEFAULT_LINK_RULES.callNoteWindowSec,
  phone: {
    min_digits: DEFAULT_LINK_RULES.phone.minDigits,
    default_calling_code: DEFAULT_LINK_RULES.phone.defaultCallingCode ?? '',
    strip_trunk_prefix: DEFAULT_LINK_RULES.phone.stripTrunkPrefix,
  },
}

export const DEFAULT_AUDIT_STAGES: AuditStage[] = [...AUDIT_STAGES]

/** The layer's rules from the file's section — `withLinkRules`, never a second type. */
export function linkRulesOf(rules: AuditRules): LinkRules {
  return withLinkRules({
    linkWindowDays: rules.link_window_days,
    openCycleReachDays: rules.open_cycle_reach_days,
    callOnlyLead: rules.call_only_lead,
    mergeOverlappingDeals: rules.merge_overlapping_deals,
    multiCustomerTiebreak: rules.multi_customer_tiebreak,
    identifierConfidence: rules.identifier_confidence,
    managerExtensions: rules.manager_extensions,
    phone: {
      minDigits: rules.phone.min_digits,
      defaultCallingCode:
        rules.phone.default_calling_code === '' ? null : rules.phone.default_calling_code,
      stripTrunkPrefix: rules.phone.strip_trunk_prefix,
    },
    callNoteWindowSec: rules.call_note_window_sec,
  })
}

/* ---------------------------------------------------------------- errors */

export class AuditTomlError extends Error {
  readonly line: number | null
  constructor(message: string, line: number | null = null) {
    super(line === null ? message : `${message} (line ${line})`)
    this.name = 'AuditTomlError'
    this.line = line
  }
}

/* ---------------------------------------------------------------- parser */

type TomlScalar = string | number | boolean
type TomlValue = TomlScalar | TomlScalar[]
type TomlTable = { [key: string]: TomlValue | TomlTable }

function isTable(value: unknown): value is TomlTable {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function unescapeBasic(raw: string, line: number): string {
  let out = ''
  for (let i = 0; i < raw.length; i += 1) {
    const ch = raw[i]!
    if (ch !== '\\') {
      out += ch
      continue
    }
    const next = raw[i + 1]
    i += 1
    switch (next) {
      case '"':
        out += '"'
        break
      case '\\':
        out += '\\'
        break
      case 'n':
        out += '\n'
        break
      case 't':
        out += '\t'
        break
      case 'r':
        out += '\r'
        break
      case 'u': {
        const hex = raw.slice(i + 1, i + 5)
        if (!/^[0-9a-fA-F]{4}$/.test(hex)) throw new AuditTomlError('bad \\u escape', line)
        out += String.fromCharCode(Number.parseInt(hex, 16))
        i += 4
        break
      }
      default:
        throw new AuditTomlError(`unknown escape \\${next ?? ''}`, line)
    }
  }
  return out
}

/** Reads one basic string starting at `text[start] === '"'`; returns the value and the index after the closing quote. */
function readString(text: string, start: number, line: number): [string, number] {
  let i = start + 1
  let raw = ''
  while (i < text.length) {
    const ch = text[i]!
    if (ch === '\\') {
      raw += ch + (text[i + 1] ?? '')
      i += 2
      continue
    }
    if (ch === '"') return [unescapeBasic(raw, line), i + 1]
    raw += ch
    i += 1
  }
  throw new AuditTomlError('unterminated string', line)
}

function readScalar(text: string, line: number): TomlScalar {
  const trimmed = text.trim()
  if (trimmed.startsWith('"')) {
    const [value, end] = readString(trimmed, 0, line)
    if (trimmed.slice(end).trim() !== '')
      throw new AuditTomlError('trailing text after string', line)
    return value
  }
  if (trimmed === 'true') return true
  if (trimmed === 'false') return false
  if (/^[+-]?(\d+)(\.\d+)?([eE][+-]?\d+)?$/.test(trimmed)) return Number(trimmed)
  throw new AuditTomlError(`unreadable value ${JSON.stringify(trimmed)}`, line)
}

function readArray(text: string, line: number): TomlScalar[] {
  const inner = text.trim().slice(1, -1)
  const items: TomlScalar[] = []
  let i = 0
  while (i < inner.length) {
    while (i < inner.length && /[\s,]/.test(inner[i]!)) i += 1
    if (i >= inner.length) break
    if (inner[i] === '"') {
      const [value, end] = readString(inner, i, line)
      items.push(value)
      i = end
    } else {
      let j = i
      while (j < inner.length && inner[j] !== ',') j += 1
      items.push(readScalar(inner.slice(i, j), line))
      i = j
    }
  }
  return items
}

function readValue(text: string, line: number): TomlValue {
  const trimmed = text.trim()
  if (trimmed.startsWith('[')) {
    if (!trimmed.endsWith(']')) throw new AuditTomlError('unterminated array', line)
    return readArray(trimmed, line)
  }
  return readScalar(trimmed, line)
}

function readKey(text: string, line: number): string {
  const trimmed = text.trim()
  if (trimmed.startsWith('"')) {
    const [value, end] = readString(trimmed, 0, line)
    if (trimmed.slice(end).trim() !== '') throw new AuditTomlError('bad quoted key', line)
    return value
  }
  if (!/^[A-Za-z0-9_-]+$/.test(trimmed))
    throw new AuditTomlError(`bad key ${JSON.stringify(trimmed)}`, line)
  return trimmed
}

function tablePath(header: string, line: number): string[] {
  const inner = header.trim().slice(1, -1).trim()
  const parts: string[] = []
  let i = 0
  while (i < inner.length) {
    while (i < inner.length && /\s/.test(inner[i]!)) i += 1
    if (inner[i] === '"') {
      const [value, end] = readString(inner, i, line)
      parts.push(value)
      i = end
    } else {
      let j = i
      while (j < inner.length && inner[j] !== '.') j += 1
      parts.push(readKey(inner.slice(i, j), line))
      i = j
    }
    while (i < inner.length && /\s/.test(inner[i]!)) i += 1
    if (inner[i] === '.') i += 1
    else if (i < inner.length) throw new AuditTomlError('bad table header', line)
  }
  if (parts.length === 0) throw new AuditTomlError('empty table header', line)
  return parts
}

/**
 * The canonical subset this module writes: `[tables]` with dotted paths,
 * `key = value` with basic strings, integers and floats, booleans, and
 * single-line arrays of scalars. Comments and blank lines are skipped.
 * Everything the writer does not produce is a parse error, on purpose.
 */
export function parseToml(text: string): TomlTable {
  const root: TomlTable = {}
  let current: TomlTable = root
  const lines = text.split(/\r?\n/)
  for (let n = 0; n < lines.length; n += 1) {
    const line = n + 1
    const raw = lines[n]!
    const stripped = raw.trim()
    if (stripped === '' || stripped.startsWith('#')) continue
    if (stripped.startsWith('[')) {
      if (!stripped.endsWith(']') || stripped.startsWith('[['))
        throw new AuditTomlError('bad table header', line)
      current = root
      for (const part of tablePath(stripped, line)) {
        const existing = current[part]
        if (existing === undefined) {
          const table: TomlTable = {}
          current[part] = table
          current = table
        } else if (isTable(existing)) {
          current = existing
        } else {
          throw new AuditTomlError(`${part} is a value, not a table`, line)
        }
      }
      continue
    }
    const eq = stripped.startsWith('"')
      ? stripped.indexOf('=', readString(stripped, 0, line)[1])
      : stripped.indexOf('=')
    if (eq < 0) throw new AuditTomlError('expected key = value', line)
    const key = readKey(stripped.slice(0, eq), line)
    if (key in current) throw new AuditTomlError(`duplicate key ${key}`, line)
    current[key] = readValue(stripped.slice(eq + 1), line)
  }
  return root
}

/* ---------------------------------------------------------------- writer */

function quote(value: string): string {
  return JSON.stringify(value)
}

function bareOrQuoted(key: string): string {
  return /^[A-Za-z0-9_-]+$/.test(key) ? key : quote(key)
}

function scalar(value: TomlScalar): string {
  if (typeof value === 'string') return quote(value)
  if (typeof value === 'boolean') return value ? 'true' : 'false'
  if (!Number.isFinite(value)) throw new AuditTomlError(`cannot write ${String(value)}`)
  return String(value)
}

function line(key: string, value: TomlValue): string {
  if (Array.isArray(value)) return `${bareOrQuoted(key)} = [${value.map(scalar).join(', ')}]`
  return `${bareOrQuoted(key)} = ${scalar(value)}`
}

/** Sorted keys where the set is the tenant's — a hash over the text must not depend on insertion order. */
function sortedEntries<T>(record: Record<string, T>): Array<[string, T]> {
  return Object.keys(record)
    .sort()
    .map((key) => [key, record[key]!])
}

/** The canonical text of a document. */
export function serializeAuditToml(doc: AuditToml): string {
  const out: string[] = []
  const section = (name: string, entries: Array<[string, TomlValue | null | undefined]>) => {
    if (out.length > 0) out.push('')
    out.push(`[${name}]`)
    for (const [key, value] of entries) {
      if (value === null || value === undefined) continue
      out.push(line(key, value))
    }
  }

  section('audit', [
    ['id', doc.audit.id],
    ['title', doc.audit.title],
    ['created_at', doc.audit.created_at],
  ])
  section('slice', [
    ['pipelines', doc.slice.pipelines],
    ['period_from', doc.slice.period_from],
    ['period_to', doc.slice.period_to],
    ['cutoff_at', doc.slice.cutoff_at],
    ['snapshot_at', doc.slice.snapshot_at],
    ['business_hours', doc.slice.business_hours],
  ])
  section('stages', [
    ['enabled', doc.stages.enabled],
    ['test_run_n', doc.stages.test_run_n],
  ])
  section('providers', [
    ['asr', doc.providers.asr],
    ['llm', doc.providers.llm],
    ['llm_model', doc.providers.llm_model],
  ])
  section('quota', [
    ['asr_minutes', doc.quota.asr_minutes],
    ['llm_tokens', doc.quota.llm_tokens],
    ['amount', doc.quota.amount],
    ['currency', doc.quota.currency],
  ])
  section('rules', [
    ['link_window_days', doc.rules.link_window_days],
    ['open_cycle_reach_days', doc.rules.open_cycle_reach_days],
    ['call_only_lead', doc.rules.call_only_lead],
    ['product_key_source', doc.rules.product_key_source],
    ['merge_overlapping_deals', doc.rules.merge_overlapping_deals],
    ['manager_extensions', doc.rules.manager_extensions],
    ['multi_customer_tiebreak', doc.rules.multi_customer_tiebreak],
    ['review_budget_hours', doc.rules.review_budget_hours],
    ['replay_from', doc.rules.replay_from],
    ['call_note_window_sec', doc.rules.call_note_window_sec],
  ])
  section('rules.identifier_confidence', sortedEntries(doc.rules.identifier_confidence))
  section('rules.phone', [
    ['min_digits', doc.rules.phone.min_digits],
    ['default_calling_code', doc.rules.phone.default_calling_code],
    ['strip_trunk_prefix', doc.rules.phone.strip_trunk_prefix],
  ])
  section('versions', [
    ['schema', doc.versions.schema],
    ['rules', doc.versions.rules],
    ['materials', doc.versions.materials],
  ])
  for (const [kind, adapter] of sortedEntries(doc.versions.adapters)) {
    section(`versions.adapters.${bareOrQuoted(kind)}`, [
      ['commit', adapter.commit],
      ['slot_mapping', adapter.slot_mapping],
    ])
  }
  section('versions.prompts', sortedEntries(doc.versions.prompts))
  return `${out.join('\n')}\n`
}

/* --------------------------------------------------------------- reading */

function str(table: TomlTable, key: string, fallback: string | null): string | null {
  const value = table[key]
  if (value === undefined) return fallback
  if (typeof value !== 'string') throw new AuditTomlError(`${key} must be a string`)
  return value
}

function num(table: TomlTable, key: string, fallback: number): number {
  const value = table[key]
  if (value === undefined) return fallback
  if (typeof value !== 'number') throw new AuditTomlError(`${key} must be a number`)
  return value
}

function bool(table: TomlTable, key: string, fallback: boolean): boolean {
  const value = table[key]
  if (value === undefined) return fallback
  if (typeof value !== 'boolean') throw new AuditTomlError(`${key} must be a boolean`)
  return value
}

function strings(table: TomlTable, key: string, fallback: string[]): string[] {
  const value = table[key]
  if (value === undefined) return fallback
  if (!Array.isArray(value) || value.some((item) => typeof item !== 'string'))
    throw new AuditTomlError(`${key} must be an array of strings`)
  return value as string[]
}

function sub(table: TomlTable, key: string): TomlTable {
  const value = table[key]
  if (value === undefined) return {}
  if (!isTable(value)) throw new AuditTomlError(`[${key}] must be a table`)
  return value
}

function required(table: TomlTable, key: string): string {
  const value = str(table, key, null)
  if (value === null) throw new AuditTomlError(`${key} is required`)
  return value
}

/** The §6.6 keys, closed: a key the writer never produces is refused by name. */
export const RULES_KEYS = [
  'link_window_days',
  'open_cycle_reach_days',
  'call_only_lead',
  'product_key_source',
  'merge_overlapping_deals',
  'manager_extensions',
  'identifier_confidence',
  'multi_customer_tiebreak',
  'review_budget_hours',
  'replay_from',
  'call_note_window_sec',
  'phone',
] as const

export const PHONE_RULES_KEYS = [
  'min_digits',
  'default_calling_code',
  'strip_trunk_prefix',
] as const

function rulesOf(table: TomlTable): AuditRules {
  for (const key of Object.keys(table)) {
    if (!(RULES_KEYS as readonly string[]).includes(key))
      throw new AuditTomlError(`unknown [rules] key ${key}`)
  }
  for (const key of Object.keys(sub(table, 'phone'))) {
    if (!(PHONE_RULES_KEYS as readonly string[]).includes(key))
      throw new AuditTomlError(`unknown [rules.phone] key ${key}`)
  }
  const callOnly = str(table, 'call_only_lead', DEFAULT_AUDIT_RULES.call_only_lead)
  if (callOnly !== 'allow' && callOnly !== 'anomaly')
    throw new AuditTomlError('call_only_lead must be allow or anomaly')
  const tiebreak = str(table, 'multi_customer_tiebreak', 'first_seen')
  if (tiebreak !== 'first_seen')
    throw new AuditTomlError('multi_customer_tiebreak must be first_seen')
  const confidence: Record<string, number> = {}
  for (const [key, value] of Object.entries(sub(table, 'identifier_confidence'))) {
    if (typeof value !== 'number')
      throw new AuditTomlError(`identifier_confidence.${key} must be a number`)
    confidence[key] = value
  }
  const phone = sub(table, 'phone')
  return {
    link_window_days: num(table, 'link_window_days', DEFAULT_AUDIT_RULES.link_window_days),
    open_cycle_reach_days: num(
      table,
      'open_cycle_reach_days',
      DEFAULT_AUDIT_RULES.open_cycle_reach_days,
    ),
    call_only_lead: callOnly,
    product_key_source: required0(
      table,
      'product_key_source',
      DEFAULT_AUDIT_RULES.product_key_source,
    ),
    merge_overlapping_deals: bool(
      table,
      'merge_overlapping_deals',
      DEFAULT_AUDIT_RULES.merge_overlapping_deals,
    ),
    manager_extensions: strings(table, 'manager_extensions', []),
    identifier_confidence:
      Object.keys(confidence).length === 0
        ? { ...DEFAULT_AUDIT_RULES.identifier_confidence }
        : confidence,
    multi_customer_tiebreak: 'first_seen',
    review_budget_hours: num(table, 'review_budget_hours', DEFAULT_AUDIT_RULES.review_budget_hours),
    replay_from: required0(table, 'replay_from', ''),
    call_note_window_sec: num(
      table,
      'call_note_window_sec',
      DEFAULT_AUDIT_RULES.call_note_window_sec,
    ),
    phone: {
      min_digits: num(phone, 'min_digits', DEFAULT_AUDIT_RULES.phone.min_digits),
      default_calling_code: required0(phone, 'default_calling_code', ''),
      strip_trunk_prefix: bool(
        phone,
        'strip_trunk_prefix',
        DEFAULT_AUDIT_RULES.phone.strip_trunk_prefix,
      ),
    },
  }
}

function required0(table: TomlTable, key: string, fallback: string): string {
  return str(table, key, fallback) ?? fallback
}

function stagesOf(table: TomlTable): AuditStage[] {
  const enabled = strings(table, 'enabled', DEFAULT_AUDIT_STAGES)
  for (const stage of enabled) {
    if (!(AUDIT_STAGES as readonly string[]).includes(stage))
      throw new AuditTomlError(`unknown stage ${stage}`)
  }
  return enabled as AuditStage[]
}

function versionsOf(table: TomlTable): AuditVersions {
  const adapters: AuditVersions['adapters'] = {}
  for (const [kind, value] of Object.entries(sub(table, 'adapters'))) {
    if (!isTable(value)) throw new AuditTomlError(`versions.adapters.${kind} must be a table`)
    adapters[kind] = {
      commit: required0(value, 'commit', ''),
      slot_mapping: required0(value, 'slot_mapping', ''),
    }
  }
  const prompts: Record<string, string> = {}
  for (const [stage, value] of Object.entries(sub(table, 'prompts'))) {
    if (typeof value !== 'string')
      throw new AuditTomlError(`versions.prompts.${stage} must be a string`)
    prompts[stage] = value
  }
  return {
    schema: num(table, 'schema', 0),
    adapters,
    rules: required0(table, 'rules', ''),
    materials: required0(table, 'materials', ''),
    prompts,
  }
}

/** A parsed table into the typed document, defaults filled, unknowns refused where they matter. */
export function auditTomlOf(root: TomlTable): AuditToml {
  const audit = sub(root, 'audit')
  const slice = sub(root, 'slice')
  const stages = sub(root, 'stages')
  const providers = sub(root, 'providers')
  const quota = sub(root, 'quota')
  return {
    audit: {
      id: required(audit, 'id'),
      title: required0(audit, 'title', ''),
      created_at: required(audit, 'created_at'),
    },
    slice: {
      pipelines: strings(slice, 'pipelines', []),
      period_from: required(slice, 'period_from'),
      period_to: required(slice, 'period_to'),
      cutoff_at: required(slice, 'cutoff_at'),
      snapshot_at: str(slice, 'snapshot_at', null),
      business_hours: str(slice, 'business_hours', null),
    },
    stages: { enabled: stagesOf(stages), test_run_n: num(stages, 'test_run_n', 0) },
    providers: {
      asr: str(providers, 'asr', null),
      llm: str(providers, 'llm', null),
      llm_model: str(providers, 'llm_model', null),
    },
    quota: {
      asr_minutes: num(quota, 'asr_minutes', 0),
      llm_tokens: num(quota, 'llm_tokens', 0),
      amount: num(quota, 'amount', 0),
      currency: required0(quota, 'currency', 'EUR'),
    },
    rules: rulesOf(sub(root, 'rules')),
    versions: versionsOf(sub(root, 'versions')),
  }
}

export function parseAuditToml(text: string): AuditToml {
  return auditTomlOf(parseToml(text))
}

/** A fresh document with the defaults, for `createAudit`. */
export function newAuditToml(input: {
  id: string
  title: string
  createdAt: string
  pipelines: string[]
  periodFrom: string
  periodTo: string
  cutoffAt: string
  businessHours?: string | null
  stages?: AuditStage[]
  testRunN?: number
  providers?: Partial<AuditToml['providers']>
  quota?: Partial<AuditToml['quota']>
  rules?: Partial<AuditRules>
  versions?: Partial<AuditVersions>
}): AuditToml {
  return {
    audit: { id: input.id, title: input.title, created_at: input.createdAt },
    slice: {
      pipelines: [...input.pipelines],
      period_from: input.periodFrom,
      period_to: input.periodTo,
      cutoff_at: input.cutoffAt,
      snapshot_at: null,
      business_hours: input.businessHours ?? null,
    },
    stages: {
      enabled: [...(input.stages ?? DEFAULT_AUDIT_STAGES)],
      test_run_n: input.testRunN ?? 0,
    },
    providers: { asr: null, llm: null, llm_model: null, ...input.providers },
    quota: { asr_minutes: 0, llm_tokens: 0, amount: 0, currency: 'EUR', ...input.quota },
    rules: {
      ...DEFAULT_AUDIT_RULES,
      ...input.rules,
      identifier_confidence: {
        ...(input.rules?.identifier_confidence ?? DEFAULT_AUDIT_RULES.identifier_confidence),
      },
      manager_extensions: [...(input.rules?.manager_extensions ?? [])],
      phone: { ...DEFAULT_AUDIT_RULES.phone, ...input.rules?.phone },
    },
    versions: { schema: 0, adapters: {}, rules: '', materials: '', prompts: {}, ...input.versions },
  }
}
